import { useState } from 'react'
import { X, ChevronDown, ChevronRight, AlertTriangle, Target, Shield, Trophy, CheckCircle2, ClipboardCheck } from 'lucide-react'
import type { QualificationChecklist, QualYesNo } from '../types'
import { QUALIFICATION_CRITERIA, EMPTY_CHECKLIST, YES_NO_CONFIG } from '../types'

interface QualificationGuideProps {
  open: boolean
  onClose: () => void
  checklist: QualificationChecklist
  onChecklistChange: (checklist: QualificationChecklist) => void
}

interface StageProps {
  id: string
  number: string
  title: string
  icon: React.ReactNode
  color: string
  children: React.ReactNode
  defaultOpen?: boolean
  activeStage: string | null
  onToggle: (id: string) => void
}

function Stage({ id, number, title, icon, color, children, activeStage, onToggle }: StageProps) {
  const expanded = activeStage === id

  return (
    <div className="border border-[var(--border-color)] rounded-lg overflow-hidden">
      <button
        onClick={() => onToggle(id)}
        className="flex items-center gap-3 w-full px-4 py-3 text-left hover:bg-[var(--bg-tertiary)] transition-colors"
      >
        {expanded ? <ChevronDown size={14} className="text-[var(--text-muted)] shrink-0" /> : <ChevronRight size={14} className="text-[var(--text-muted)] shrink-0" />}
        <span className={`flex items-center justify-center w-6 h-6 rounded-full text-[10px] font-bold ${color}`}>{number}</span>
        <span className="flex items-center gap-2 text-sm font-medium text-[var(--text-primary)]">
          {icon}
          {title}
        </span>
      </button>
      {expanded && (
        <div className="px-4 pb-4 pt-1 border-t border-[var(--border-color)] space-y-3">
          {children}
        </div>
      )}
    </div>
  )
}

function SectionLabel({ children }: { children: React.ReactNode }) {
  return <p className="text-[10px] font-semibold text-[var(--text-muted)] uppercase tracking-wider mt-2">{children}</p>
}

function BulletList({ items }: { items: string[] }) {
  return (
    <ul className="space-y-1.5">
      {items.map((item, i) => (
        <li key={i} className="flex gap-2 text-xs text-[var(--text-secondary)] leading-relaxed">
          <span className="text-[var(--text-muted)] mt-0.5 shrink-0">-</span>
          <span>{item}</span>
        </li>
      ))}
    </ul>
  )
}

function ThinkingBox({ items }: { items: string[] }) {
  return (
    <div className="rounded-lg bg-amber-500/5 border border-amber-500/10 p-3 space-y-1.5">
      <p className="text-[10px] font-semibold text-amber-400 uppercase tracking-wider">What presales is thinking about</p>
      {items.map((item, i) => (
        <p key={i} className="text-[11px] text-[var(--text-secondary)] leading-relaxed italic">{item}</p>
      ))}
    </div>
  )
}

function OutputBox({ children }: { children: React.ReactNode }) {
  return (
    <div className="rounded-lg bg-indigo-500/5 border border-indigo-500/10 p-3">
      <p className="text-[10px] font-semibold text-indigo-400 uppercase tracking-wider mb-1">Output</p>
      <p className="text-xs text-[var(--text-secondary)] leading-relaxed">{children}</p>
    </div>
  )
}

export function QualificationGuide({ open, onClose, checklist, onChecklistChange }: QualificationGuideProps) {
  const [activeStage, setActiveStage] = useState<string | null>('0')

  const cl = { ...EMPTY_CHECKLIST, ...checklist }
  const answeredCount = QUALIFICATION_CRITERIA.filter(c => cl[c.key] !== null).length

  const toggleStage = (id: string) => setActiveStage(activeStage === id ? null : id)

  if (!open) return null

  return (
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 z-40 bg-black/40 backdrop-blur-sm" onClick={onClose} />

      {/* Drawer */}
      <div className="fixed inset-y-0 right-0 z-50 w-[520px] max-w-[90vw] bg-[var(--bg-primary)] border-l border-[var(--border-color)] shadow-2xl flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-[var(--border-color)] shrink-0">
          <div>
            <h2 className="text-sm font-semibold text-[var(--text-primary)]">RFP Qualification Guide</h2>
            <p className="text-[11px] text-[var(--text-muted)] mt-0.5">
              The default answer to a new RFP is <strong className="text-red-400">no</strong> unless we can answer three questions.
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg text-[var(--text-muted)] hover:text-[var(--text-primary)] hover:bg-[var(--bg-secondary)] transition-colors"
          >
            <X size={16} />
          </button>
        </div>

        {/* Three questions banner */}
        <div className="px-5 py-3 bg-[var(--bg-secondary)] border-b border-[var(--border-color)] shrink-0">
          <div className="grid grid-cols-3 gap-2">
            <div className="text-center p-2 rounded-lg bg-[var(--bg-primary)] border border-[var(--border-color)]">
              <Target size={14} className="mx-auto mb-1 text-blue-400" />
              <p className="text-[10px] font-medium text-[var(--text-primary)]">Is it ICP?</p>
            </div>
            <div className="text-center p-2 rounded-lg bg-[var(--bg-primary)] border border-[var(--border-color)]">
              <Shield size={14} className="mx-auto mb-1 text-amber-400" />
              <p className="text-[10px] font-medium text-[var(--text-primary)]">Risks manageable?</p>
            </div>
            <div className="text-center p-2 rounded-lg bg-[var(--bg-primary)] border border-[var(--border-color)]">
              <Trophy size={14} className="mx-auto mb-1 text-green-400" />
              <p className="text-[10px] font-medium text-[var(--text-primary)]">Right to win?</p>
            </div>
          </div>
        </div>

        {/* Qualification checklist */}
        <div className="px-5 py-4 border-b border-[var(--border-color)] shrink-0">
          <div className="flex items-center justify-between mb-3">
            <p className="text-xs font-semibold text-[var(--text-primary)]">Qualification Checklist</p>
            <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full ${
              answeredCount === QUALIFICATION_CRITERIA.length
                ? 'bg-green-500/10 text-green-400'
                : answeredCount > 0
                  ? 'bg-amber-500/10 text-amber-400'
                  : 'bg-[var(--bg-secondary)] text-[var(--text-muted)]'
            }`}>
              {answeredCount}/{QUALIFICATION_CRITERIA.length}
            </span>
          </div>
          <div className="space-y-2.5">
            {QUALIFICATION_CRITERIA.map(criterion => {
              const val = cl[criterion.key]
              return (
                <div key={criterion.key} className="flex items-center justify-between gap-3">
                  <p className="text-xs text-[var(--text-primary)] flex-1">{criterion.label}</p>
                  <div className="flex items-center gap-1 shrink-0">
                    {(['yes', 'no'] as const).map(answer => {
                      const cfg = YES_NO_CONFIG[answer]
                      const selected = val === answer
                      return (
                        <button
                          key={answer}
                          onClick={() => {
                            const next: QualYesNo = selected ? null : answer
                            onChecklistChange({ ...cl, [criterion.key]: next })
                          }}
                          className={`px-2.5 py-1 text-[11px] font-medium rounded-md border transition-colors ${
                            selected
                              ? `${cfg.bg} ${cfg.color}`
                              : 'border-[var(--border-color)] text-[var(--text-muted)] hover:bg-[var(--bg-tertiary)]'
                          }`}
                        >
                          {cfg.label}
                        </button>
                      )
                    })}
                  </div>
                </div>
              )
            })}
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-3">
          {/* Stage 0 */}
          <Stage id="0" number="0" title="Intake (within 24 hours)" icon={<ClipboardCheck size={14} />} color="bg-gray-500/10 text-gray-400" activeStage={activeStage} onToggle={toggleStage}>
            <SectionLabel>What presales does</SectionLabel>
            <BulletList items={[
              'Log the RFP in the tracker with source, deadline, estimated effort, and AE owner',
              'Read the full RFP end-to-end before any other action — not just the cover letter',
              'Note the submission deadline and work backwards to identify if the timeline is feasible',
              'Flag immediately if the RFP appears wired for an incumbent (boilerplate questions matching a specific vendor, oddly specific technical requirements, very short timelines)',
            ]} />
            <ThinkingBox items={[
              'Is this a genuine buying signal, or a mandated refresh? Government and enterprise buyers often run RFPs every 3 years regardless of satisfaction.',
              'Did we know this was coming? If it\'s a cold RFP, our win odds are structurally lower than if we shaped it.',
              'Is the AE pushing this because it\'s a real opportunity, or because it landed in their inbox?',
            ]} />
            <OutputBox>Intake summary posted to the deal channel. No work begins beyond this until Stage 1 is complete.</OutputBox>
          </Stage>

          {/* Stage 1 */}
          <Stage id="1" number="1" title="ICP Fit Check" icon={<Target size={14} />} color="bg-blue-500/10 text-blue-400" activeStage={activeStage} onToggle={toggleStage}>
            <SectionLabel>What presales does</SectionLabel>
            <BulletList items={[
              'Score the deal against each ICP dimension explicitly. Don\'t assume — confirm.',
              'For each dimension, mark: confirmed / unconfirmed / fails',
              'If any dimension is "unconfirmed," identify exactly what discovery is needed to confirm it',
            ]} />
            <SectionLabel>ICP dimensions to check</SectionLabel>
            <div className="grid grid-cols-2 gap-2">
              {[
                { name: 'Volume', desc: 'Are they doing large-scale verification volume that justifies the cost of serving them?' },
                { name: 'Multi-product', desc: 'Will they use multiple products through our orchestration layer? e.g. KYC + AML, KYC + orchestration, IDV + KYB, KYC + risk' },
                { name: 'Geography', desc: 'Do they need coverage across multiple countries where we have data source strength?' },
                { name: 'Segment', desc: 'Is their industry a segment we win and retain in, or one we historically struggle with?' },
              ].map(d => (
                <div key={d.name} className="rounded-lg border border-[var(--border-color)] bg-[var(--bg-secondary)] p-2.5">
                  <p className="text-[11px] font-semibold text-[var(--text-primary)]">{d.name}</p>
                  <p className="text-[10px] text-[var(--text-muted)] mt-0.5 leading-relaxed">{d.desc}</p>
                </div>
              ))}
            </div>
            <ThinkingBox items={[
              'A deal can be huge on one dimension and fail on another. Volume alone is not ICP.',
              'If we have to stretch the definition of ICP to make this fit, it doesn\'t fit.',
              'Be honest. The cost of a forced-fit RFP is a year of presales time on a deal we lose.',
            ]} />
            <OutputBox>ICP scorecard. If two or more dimensions fail or can't be confirmed, recommend qualify out.</OutputBox>
          </Stage>

          {/* Stage 2 */}
          <Stage id="2" number="2" title="Risk Identification & Mitigation" icon={<AlertTriangle size={14} />} color="bg-amber-500/10 text-amber-400" activeStage={activeStage} onToggle={toggleStage}>
            <SectionLabel>What presales does</SectionLabel>
            <BulletList items={[
              'Map every known risk category and assign a status: not present / present but mitigable / present and blocking',
              'For each "present but mitigable" risk, document the specific mitigation action, owner, and deadline',
              'Risks must be in motion before go decision — not added to a follow-up list',
            ]} />
            <SectionLabel>Risk categories</SectionLabel>
            <div className="space-y-1">
              {[
                { name: 'Procurement complexity', desc: 'Government vs. commercial, panel arrangements, mandated processes' },
                { name: 'Relationship coverage', desc: 'Do we know anyone inside the buyer? Can we bring a partner who does?' },
                { name: 'Implementation lift', desc: 'How heavy is the integration? Have we done one like this before?' },
                { name: 'Security & compliance', desc: 'Clearances, data residency, certifications, audit requirements' },
                { name: 'Architectural fit', desc: 'Does our deployment model match their needs? (multi-tenancy, isolation, on-prem)' },
                { name: 'Commercial structure', desc: 'Can their procurement accommodate our pricing model?' },
                { name: 'Timeline', desc: 'Is the response deadline realistic given the work required?' },
              ].map(r => (
                <div key={r.name} className="flex gap-2 px-2.5 py-1.5 rounded bg-[var(--bg-secondary)] border border-[var(--border-color)]">
                  <p className="text-[11px] font-medium text-[var(--text-primary)] shrink-0 w-36">{r.name}</p>
                  <p className="text-[10px] text-[var(--text-muted)]">{r.desc}</p>
                </div>
              ))}
            </div>
            <ThinkingBox items={[
              'Which risks are deal-breakers, and which are just friction?',
              'Is there a partner play that mitigates relationship or implementation risk?',
              'What would a senior consulting firm charge to handle the parts we can\'t?',
              'Am I listing risks honestly, or downplaying them because the AE wants to pursue?',
            ]} />
            <OutputBox>Risk register with mitigation actions in motion. If any risk is "present and blocking" without a credible path to resolution, recommend qualify out.</OutputBox>
          </Stage>

          {/* Stage 3 */}
          <Stage id="3" number="3" title="Right-to-Win Analysis" icon={<Trophy size={14} />} color="bg-green-500/10 text-green-400" activeStage={activeStage} onToggle={toggleStage}>
            <p className="text-xs text-[var(--text-secondary)]">This is where presales spends the most time. Three sub-questions, each requiring actual work.</p>

            <div className="rounded-lg border border-[var(--border-color)] bg-[var(--bg-secondary)] p-3 space-y-2">
              <p className="text-[11px] font-semibold text-[var(--text-primary)]">3a: Who are the horses in the race?</p>
              <BulletList items={[
                'List every known and likely competitor',
                'For each: current relationship to the buyer (incumbent, adjacent vendor, new entrant), likely strategy, known weaknesses',
                'Identify which competitors are structurally favoured',
              ]} />
              <ThinkingBox items={[
                'Incumbents win most RFPs. If there\'s an incumbent and they\'re not deeply unhappy, our odds are low.',
                'Price-led challengers often have nothing to lose — they\'ll undercut to win the logo.',
                'Adjacent vendors (already inside on another product) have a relationship advantage we don\'t.',
              ]} />
            </div>

            <div className="rounded-lg border border-[var(--border-color)] bg-[var(--bg-secondary)] p-3 space-y-2">
              <p className="text-[11px] font-semibold text-[var(--text-primary)]">3b: What's the AS-IS state?</p>
              <BulletList items={[
                'Document what the customer has today — vendor, capability, gaps',
                'Identify whether the customer is actually dissatisfied, or just running a mandated process',
                'Look for evidence of dissatisfaction: stated pain points, recent failures, regulatory pressure, leadership changes',
              ]} />
              <ThinkingBox items={[
                'If the customer is happy with the incumbent, this is a compliance exercise and we\'re a stalking horse for the incumbent\'s renewal pricing.',
                'Switching costs are real. Even a better product loses to "good enough and already integrated" most of the time.',
                'What would have to be true for this customer to actually switch?',
              ]} />
            </div>

            <div className="rounded-lg border border-[var(--border-color)] bg-[var(--bg-secondary)] p-3 space-y-2">
              <p className="text-[11px] font-semibold text-[var(--text-primary)]">3c: What's our differentiation?</p>
              <BulletList items={[
                'Write a specific, defensible reason this customer would choose us over each named competitor',
                'Ground the differentiation in something the customer has said matters, not something we think should matter',
                'Test the differentiation against forward-looking factors (regulatory changes, market shifts) — does our story hold up 12-24 months out?',
              ]} />
              <ThinkingBox items={[
                '"We have better technology" is not differentiation. Specific capabilities tied to specific customer outcomes are.',
                'The differentiation has to survive contact with procurement. Will it still matter when scored against a weighted criteria matrix?',
                'Are we differentiated, or are we just present?',
              ]} />
            </div>

            <OutputBox>A one-paragraph win story that names the customer's situation, the competitors, and the specific reason we win. If presales can't write this paragraph credibly, we don't have a right to win.</OutputBox>
          </Stage>

          {/* Stage 4 */}
          <Stage id="4" number="4" title="Validation Before Commit" icon={<CheckCircle2 size={14} />} color="bg-violet-500/10 text-violet-400" activeStage={activeStage} onToggle={toggleStage}>
            <p className="text-xs text-[var(--text-secondary)]">The differentiation story is a hypothesis until it's validated. This is the step that's easiest to skip and most expensive to skip.</p>
            <SectionLabel>What presales does</SectionLabel>
            <BulletList items={[
              'List every assumption underpinning the win story (e.g. "competitor X can\'t do Y," "the customer can\'t build this themselves," "regulation Z will land in this timeframe")',
              'For each assumption, identify a validation source: customer discovery call, partner intel, public information, internal SME',
              'Run the validation actions before the go decision, not after',
            ]} />
            <ThinkingBox items={[
              'If the win story rests on three assumptions and we\'ve validated zero, we\'re guessing.',
              'A 30-minute call with a partner who knows the account is worth more than a week of internal speculation.',
              'If we can\'t validate, we should weight the win probability accordingly — or qualify out.',
            ]} />
            <OutputBox>Validation log showing which assumptions are confirmed, which are still open, and what the residual risk is.</OutputBox>
          </Stage>

          {/* Stage 5 */}
          <Stage id="5" number="5" title="Go / No-Go Recommendation" icon={<ClipboardCheck size={14} />} color="bg-emerald-500/10 text-emerald-400" activeStage={activeStage} onToggle={toggleStage}>
            <SectionLabel>What presales does</SectionLabel>
            <BulletList items={[
              'Produce a one-page recommendation covering: ICP fit, risk posture, right-to-win summary, validation status, estimated effort, estimated win probability',
              'Make a clear recommendation: go, no-go, or conditional go (with conditions named)',
              'Present to sales leadership for sign-off before any response work begins',
            ]} />

            <SectionLabel>Decision logic</SectionLabel>
            <div className="space-y-1.5">
              <div className="flex items-start gap-2 p-2.5 rounded-lg bg-green-500/5 border border-green-500/10">
                <span className="text-[10px] font-bold text-green-400 shrink-0 mt-0.5 w-16">GO</span>
                <p className="text-[11px] text-[var(--text-secondary)]">ICP confirmed on all dimensions, risks identified with mitigations in motion, defensible right-to-win story with validated assumptions</p>
              </div>
              <div className="flex items-start gap-2 p-2.5 rounded-lg bg-amber-500/5 border border-amber-500/10">
                <span className="text-[10px] font-bold text-amber-400 shrink-0 mt-0.5 w-16">COND. GO</span>
                <p className="text-[11px] text-[var(--text-secondary)]">The above is true except for specific named gaps that have a clear closure path within the response timeline</p>
              </div>
              <div className="flex items-start gap-2 p-2.5 rounded-lg bg-red-500/5 border border-red-500/10">
                <span className="text-[10px] font-bold text-red-400 shrink-0 mt-0.5 w-16">NO-GO</span>
                <p className="text-[11px] text-[var(--text-secondary)]">Any of the above fails without a credible path to resolution</p>
              </div>
            </div>

            <ThinkingBox items={[
              'The qualification conversation itself is part of the work — it\'s not a quick yes/no.',
              'Saying no to a bad RFP frees up capacity to shape a good one.',
              'The cost of a forced "yes" is paid by every other deal in the pipeline.',
            ]} />
          </Stage>
        </div>

        {/* Footer */}
        <div className="px-5 py-3 border-t border-[var(--border-color)] bg-[var(--bg-secondary)] shrink-0">
          <p className="text-[10px] text-[var(--text-muted)] text-center">
            Every RFP response costs real money — senior time, SME time, opportunity cost on deals we could be shaping instead.
          </p>
        </div>
      </div>
    </>
  )
}
